import {Component, OnInit, AfterViewInit, Input, ViewChild, ElementRef, ChangeDetectorRef, HostListener, OnDestroy} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../../environments/environment';
import html2canvas from 'html2canvas';
import {getHtml2CanvasOptionsForDocumentExport, saveCanvasAsPdfDocument} from '../document-pdf-export.util';
import * as feather from 'feather-icons';
import { DocumentPayload } from '../../../../models/document';
import { DocumentService } from '../../../../services/document/document.service';
import { ModalRightService } from '../../../../services/modal-right/modal-right.service';
import { StationService } from '../../../../services/station.service';
import { getStationIdFromLocalStorage } from '../../../../services/parametre-document/parametre-document-state.service';
import { resolveEnterpriseDisplayName } from '../../../../services/enterprise-context';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';

export interface Product {
    designation: string;
    quantite: string;
    disponible: string;
    unite: string;
    total: string;
}

@Component({
    selector: 'app-apercue',
    templateUrl: './apercue.component.html',
    styleUrls: ['./apercue.component.scss']
})
export class ApercueComponent implements OnInit, AfterViewInit,OnDestroy {
    @ViewChild('documentContainer') documentContainer!: ElementRef;
    private destroy$ = new Subject<void>();
    @Input() selectedItem: any = null;
    @Input() documentTypeInput?: string;

    today: Date = new Date();
    isSettingsPanelOpen = false;
    file: string = environment.file_url || 'assets/images/logo.png';
    urlImages: string = environment.url_images;
    stationId = getStationIdFromLocalStorage();
    stationLogo: string | null = null;
    documentLogoBroken = false;
    stationName: string = resolveEnterpriseDisplayName();
    documentType: string = 'DEVIS';
    documentId: string = 'DOC-0001';
    isExportingPDF = false;

    // Données dynamiques affichées
    partyName: string = '--';
    partyEmail: string = '--';
    partyPhone: string = '--';
    partyAddress: string = '--';
    warehouse: string = 'Entrepôt principal';
    deliveryDate: string = '--';
    documentDate: string = '--';
    documentStatut: string = '--';

    showTotals = true;
    showContactInfo = true;
    showDescription = false;
    noteText = '';

    isCustomizerOpen = false;
    customizerWidth = '0%';
    sidebarOpened = false;

    products: Product[] = [];
    totals = {
        totalHT: 0,
        totalTVA: 0,
        totalTTC: 0,
        timbreFiscal: 0,
        netAPayer: 0
    };

    private componentInitialized = false;

    constructor(
        private router: Router,
        private cdr: ChangeDetectorRef,
        private toastr: ToastrService,
        private route: ActivatedRoute,
        private documentService: DocumentService,
        private customizerService: ModalRightService,
        private stationService: StationService
    ) {}

    ngOnInit(): void {
        this.detectDocumentTypeFromRoute();
        this.loadStationData();

        // Écoute les options (inchangé)
        this.customizerService.sidebarData$.subscribe(data => {
            this.showTotals = data.showTotals;
            this.showContactInfo = data.showContactInfo;
            this.showDescription = data.showDescription;
            this.noteText = data.noteText;
        });

        // NOUVEAU : écoute le vrai document chargé par le service
        this.documentService.currentPreviewDocument$
            .pipe(takeUntil(this.destroy$))
            .subscribe(doc => {
                if (doc) {
                    this.selectedItem = doc;
                    this.initializeData();
                    this.cdr.detectChanges();
                }
            });

        // Gestion ouverture/fermeture
        this.customizerService.isCustomizerOpen$.subscribe(open => {
            this.isCustomizerOpen = open;
            this.customizerWidth = open ? '58%' : '0%';

            // Optionnel : nettoyer à la fermeture
            if (!open) {
                this.documentService.clearCurrentPreviewDocument();
            }
        });
    }

    private loadStationData(): void {
        if (!this.stationId) {
            return;
        }

        this.stationService.getStation(this.stationId).subscribe({
            next: (response: any) => {
                const station = response?.station;
                if (!station) {
                    return;
                }

                this.stationName = resolveEnterpriseDisplayName(
                    station.nom_entreprise || station.label
                );
                this.stationLogo = this.resolveStationLogoUrl(station.logo);
                this.documentLogoBroken = false;
                this.cdr.detectChanges();
            },
            error: () => {
                this.stationLogo = null;
            }
        });
    }

    private resolveStationLogoUrl(logo?: string | null): string | null {
        const raw = String(logo ?? '').trim();
        if (!raw || raw.endsWith('/')) {
            return null;
        }
        if (raw.startsWith('http://') || raw.startsWith('https://')) {
            return raw;
        }
        const logoPath = raw.startsWith('/') ? raw : `/${raw}`;
        return `${this.urlImages}${logoPath}`;
    }

    onDocumentLogoError(): void {
        this.documentLogoBroken = true;
        this.cdr.detectChanges();
    }

    getDocumentLogoInitials(): string {
        const words = String(this.stationName || 'Entreprise')
            .trim()
            .split(/\s+/)
            .filter(Boolean);
        return words.slice(0, 2).map(word => word.charAt(0).toUpperCase()).join('') || 'ET';
    }
    ngAfterViewInit(): void {
        this.componentInitialized = true;
        if (typeof feather !== 'undefined') {
            feather.replace();
        }
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.documentService.clearCurrentPreviewDocument();
    }
    private loadDocument(id: number): void {
        this.documentService.getDocumentById(id).subscribe({
            next: (res) => {
                this.selectedItem = res?.data?.document || res;
                this.initializeData();
                this.openSettingsPanel();
            },
            error: () => {
                this.toastr.error('Document introuvable');
                this.initializeDefaultData();
                this.openSettingsPanel();
            }
        });
    }

    openSettingsPanel() {
        if (!this.componentInitialized) {
            this.cdr.detectChanges();
            setTimeout(() => this.openSettingsPanel(), 100);
            return;
        }

        this.isSettingsPanelOpen = true;
        if (this.selectedItem) {
            this.initializeData();
        } else {
            this.initializeDefaultData();
        }
        this.cdr.detectChanges();
    }

    closeSettingsPanel() {
        this.isSettingsPanelOpen = false;
        this.cdr.detectChanges();
    }

    private initializeData() {
        // === ÉTAPE 1 : EXTRAIRE LE VRAI DOCUMENT (car tu reçois item.fullData) ===
        if (this.selectedItem && (this.selectedItem as any).fullData) {
            this.selectedItem = (this.selectedItem as any).fullData;
        }

        if (!this.selectedItem) {
            this.initializeDefaultData();
            return;
        }

        // === ÉTAPE 2 : TYPE + RÉFÉRENCE ===
        // Utiliser ref_externe depuis l'API, avec fallback sur reference si nécessaire
        this.documentId = this.selectedItem.ref_externe || 
                         (this.selectedItem as any).reference || 
                         (this.selectedItem as any).document?.ref_externe || 
                         'DOC-0001';
        console.log('[ApercueComponent] documentId (ref_externe):', this.documentId);
        this.detectDocumentTypeFromId(this.selectedItem.type_document_id);
        
        // Date du document
        this.documentDate = this.selectedItem.date || 
                           (this.selectedItem as any).date_document || 
                           (this.selectedItem as any).document?.date ||
                           '--';
        
        // Statut du document - peut être un objet avec titre/nom ou directement une string
        const statut = (this.selectedItem as any).statut || 
                      (this.selectedItem as any).document?.statut ||
                      (this.selectedItem as any).statut_data;
        if (statut) {
            if (Array.isArray(statut) && statut.length > 0) {
                this.documentStatut = statut[0].titre || statut[0].nom || '--';
            } else if (typeof statut === 'object') {
                this.documentStatut = statut.titre || statut.nom || statut.libelle || '--';
            } else if (typeof statut === 'string') {
                this.documentStatut = statut;
            } else {
                this.documentStatut = '--';
            }
        } else {
            this.documentStatut = '--';
        }
        console.log('[ApercueComponent] Date:', this.documentDate, 'Statut:', this.documentStatut);

        // === ÉTAPE 3 : CLIENT ===
        if (this.isVenteDocument()) {
            const client = this.selectedItem.clients?.[0] ||
                this.selectedItem.client_data ||
                (this.selectedItem as any).client;
            this.partyName = client?.nom_client || client?.nom_prenom || 'Client inconnu';
            this.partyEmail = client?.email || '--';
            this.partyPhone = client?.telephone || client?.phone || '--';
            this.partyAddress = client?.adresse || '--';
        }

        // === ÉTAPE 4 : PRODUITS — LA CLÉ QUI MANQUAIT ===
        // Récupérer les lignes depuis différentes structures possibles de l'API
        const lignes = (this.selectedItem as any).lignesDocument || 
                      (this.selectedItem as any).lignes_document ||
                      (this.selectedItem as any).document?.lignesDocument ||
                      (this.selectedItem as any).document?.lignes_document ||
                      [];
        console.log('[ApercueComponent] Lignes trouvées:', lignes);

        this.products = lignes.map((ligne: any) => {
            const qte = parseFloat(ligne.qnt || '0') || 0;
            const prix = parseFloat(ligne.prix_ht || '0') || 0;

            return {
                designation: (ligne.libelle || 'Sans designation').trim(),
                quantite: qte.toString(),
                disponible: '-',
                unite: this.formatPrice(prix),
                total: this.formatPrice(qte * prix)
            };
        });

        // Si pas de produits → fallback visible
        if (this.products.length === 0) {
            this.products = [{ designation: 'Aucun article', quantite: '0', disponible: '-', unite: '0.000 TND', total: '0.000 TND' }];
        }

        // === ÉTAPE 5 : TOTAUX (déjà bon) ===
        // Récupérer les totaux depuis différentes structures possibles
        const doc = this.selectedItem as any;
        this.totals = {
            totalHT: this.parsePrice(doc.total_ht || doc.document?.total_ht || '0'),
            totalTVA: this.parsePrice(doc.total_tva || doc.document?.total_tva || '0'),
            totalTTC: this.parsePrice(doc.total_ttc || doc.document?.total_ttc || '0'),
            timbreFiscal: this.parsePrice(doc.timbre_fiscal || doc.document?.timbre_fiscal || '0'),
            netAPayer: this.parsePrice(doc.net_payer || doc.document?.net_payer || doc.net_payer || '0')
        };
        console.log('[ApercueComponent] Totaux:', this.totals);

        // === ÉTAPE 6 : AUTRES INFOS ===
        this.warehouse = this.selectedItem.station_id ? `Station ${this.selectedItem.station_id}` : 'Entrepôt principal';
        this.deliveryDate = this.selectedItem.date_livraison || '--';

        this.cdr.detectChanges();
    }

    private detectDocumentTypeFromId(id: number) {
        const typeMap: { [key: number]: string } = {
            7: 'DEVIS',
            8: 'COMMANDE CLIENT',
            11: 'FACTURE',
            10: 'BON DE LIVRAISON',
            9: 'BON DE SORTIE',
            12: 'COMMANDE FOURNISSEUR',
            13: 'BON RECEPTION',
            14: 'FACTURE FOURNISSEUR'
        };

        this.documentType = typeMap[id] || 'DEVIS';
    }

    private isVenteDocument(): boolean {
        return ['DEVIS', 'COMMANDE CLIENT', 'FACTURE', 'BON DE LIVRAISON', 'BON DE SORTIE'].includes(this.documentType);
    }

    private initializeDefaultData(): void {
        this.partyName = 'Client par défaut';
        this.products = ((this.selectedItem as any).lignesDocument || []).map((ligne: any) => {
            const qte = Number(ligne.qnt || ligne.quantite || 0);
            const prix = Number(ligne.prix_ht || ligne.prix_unitaire || ligne.unitPrice || 0);
            return {
                designation: ligne.libelle || ligne.designation || ligne.nom || 'Article sans nom',
                quantite: qte.toString(),
                disponible: '--',
                unite: this.formatPrice(prix),
                total: this.formatPrice(qte * prix)
            };
        });
        this.totals = { totalHT: 500, totalTVA: 100, totalTTC: 600, timbreFiscal: 0.6, netAPayer: 600.6 };
    }

    // === UTILITAIRES ===
    private parsePrice(value: string | undefined): number {
        if (!value) return 0;
        return parseFloat(value.replace(/[^0-9.-]/g, '')) || 0;
    }

    private formatPrice(value: number | string): string {
        const num = typeof value === 'string' ? parseFloat(value) || 0 : value;
        return num.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + ' TND';
    }

    async downloadPDF() {


        if (!this.documentContainer) {
            this.toastr.error('Contenu de l\'aperçu non disponible.');
            return;
        }

        this.isExportingPDF = true;
        this.toastr.info(`Génération du PDF ${this.documentType} #${this.documentId} en cours...`, '', {
            timeOut: 0,
            progressBar: true
        });

        try {
            const element = this.documentContainer.nativeElement;
            if (!this.isSettingsPanelOpen) {
                this.openSettingsPanel();
                await new Promise(resolve => setTimeout(resolve, 300));
            }
            const overlay = element.querySelector('.pdf-export-overlay');
            const overlayElement = overlay ? overlay as HTMLElement : null;
            const wasOverlayVisible = overlayElement ? overlayElement.style.display !== 'none' : false;
            if (overlayElement) {
                overlayElement.style.display = 'none';
            }
            const elementsToHide = element.querySelectorAll('.close-btn, .settings-overlay');
            const originalDisplays: string[] = [];

            elementsToHide.forEach((el) => {
                const htmlEl = el as HTMLElement;
                originalDisplays.push(htmlEl.style.display || '');
                htmlEl.style.display = 'none';
            });
            this.cdr.detectChanges();
            await new Promise(resolve => setTimeout(resolve, 200));
            const canvas = await html2canvas(
                element,
                Object.assign({}, getHtml2CanvasOptionsForDocumentExport(element as HTMLElement), {
                    onclone: (clonedDoc: Document) => {
                        const overlays = clonedDoc.querySelectorAll('.pdf-export-overlay, .settings-overlay, .close-btn, .pdf-export-loader');
                        overlays.forEach(overlay => {
                            (overlay as HTMLElement).style.display = 'none';
                            (overlay as HTMLElement).style.visibility = 'hidden';
                        });
                        const header = clonedDoc.querySelector('.settings-header');
                        if (header) {
                            (header as HTMLElement).style.display = 'flex';
                        }
                    }
                })
            );

            elementsToHide.forEach((el, index) => {
                (el as HTMLElement).style.display = originalDisplays[index] || '';
            });

            if (overlayElement && wasOverlayVisible) {
                overlayElement.style.display = 'flex';
            }

            this.cdr.detectChanges();
            const date = new Date().toISOString().split('T')[0];
            const cleanDocumentType = this.documentType.toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-]/g, '');
            const cleanDocumentId = this.documentId
                .replace(/[^a-zA-Z0-9]/g, '')
                .substring(0, 20);
            const filename = `${cleanDocumentType}-${cleanDocumentId}-${date}.pdf`;
            saveCanvasAsPdfDocument(canvas, filename);
            this.toastr.success(`PDF "${this.documentType} #${this.documentId}" téléchargé avec succès !`, 'Succès');

        } catch (error) {
            this.toastr.error(`Erreur lors de la génération du PDF ${this.documentType}: ${error.message || 'Erreur inconnue'}`, 'Erreur');
        } finally {
            const overlay = this.documentContainer?.nativeElement?.querySelector('.pdf-export-overlay');
            const overlayElement = overlay ? overlay as HTMLElement : null;
            if (overlayElement) {
                overlayElement.style.display = this.isExportingPDF ? 'flex' : 'none';
            }

            this.isExportingPDF = false;
            this.cdr.detectChanges();
        }
    }

    private detectDocumentTypeFromRoute(): void {
        const url = this.router.url.toLowerCase();
        const map: { [key: string]: string } = {
            'devis': 'DEVIS',
            'commande-client': 'COMMANDE CLIENT',
            'facture': 'FACTURE',
            'bon-livraison': 'BON DE LIVRAISON',
            'bon-sortie': 'BON DE SORTIE',
            'commande-fournisseur': 'COMMANDE FOURNISSEUR',
            'bon-reception': 'BON RECEPTION',
            'facture-fournisseur': 'FACTURE FOURNISSEUR'
        };
        for (const [key, value] of Object.entries(map)) {
            if (url.includes(key)) {
                this.documentType = value;
                return;
            }
        }
        this.documentType = 'DEVIS';
    }

    @HostListener('document:click', ['$event'])
    onOutsideClick(event: MouseEvent) {
        const sidebar = document.querySelector('.customizer-content');
        const clickedInside = sidebar?.contains(event.target as Node);
        const clickedOnTableRow = (event.target as HTMLElement).closest('tr') !== null;
        if (this.isCustomizerOpen && !clickedInside && !clickedOnTableRow) {
            this.customizerService.close();
        }
    }

    toggleCustomizer() {
        this.customizerService.close();
    }
}
